import { HorizontalSection } from '@/components/display/horizontal-section';
import { SearchBar } from '@/components/forms/search-bar';
import { ScreenHeader } from '@/components/layout/screen-header';
import { TAB_BOTTOM_SPACING, TabScreenWrapper } from '@/components/layout/tab-screen-wrapper';
import CuisineChip from '@/components/ui/cuisine-chip';
import { ProgressiveChecklist } from '@/components/ui/progressive-checklist';
import { useTourTargets } from '@/features/feature-tours';
import { SuggestedFriends } from '@/features/friends/components/suggested-friends';
import { ExistingUserTastePrompt } from '@/features/onboarding/components/existing-user-taste-prompt';
import { useExistingUserPrompt } from '@/features/onboarding/hooks/use-existing-user-prompt';
import { useProgressiveChecklist } from '@/features/onboarding/hooks/use-progressive-checklist';
import { useUserLocation } from '@/hooks/use-user-location';
import { useAppTheme } from '@/providers/theme-provider';
import { useUnits } from '@/providers/units-provider';
import { router } from 'expo-router';
import React from 'react';
import { ActivityIndicator, ScrollView, View } from 'react-native';
import { DiscoverEmptyState } from './components/empty-state';
import { RestaurantSection } from './components/restaurant-section';
import { SearchTypeahead } from './components/search-typeahead';
import { useDiscoverActions, useDiscoverData, useDiscoverSearch } from './hooks';

export function DiscoverScreen() {
  const theme = useAppTheme();
  const units = useUnits();
  const { coords, defaultLocation } = useUserLocation();
  const { registerTarget } = useTourTargets();
  const checklist = useProgressiveChecklist();
  const existingUserPrompt = useExistingUserPrompt();

  const nearbyLat = coords?.latitude ?? defaultLocation.latitude;
  const nearbyLng = coords?.longitude ?? defaultLocation.longitude;

  const data = useDiscoverData({ latitude: nearbyLat, longitude: nearbyLng });
  const search = useDiscoverSearch();
  const actions = useDiscoverActions({
    searchQuery: search.searchQuery,
    debouncedQuery: search.debouncedQuery,
    setIsSearchFocused: search.setIsSearchFocused,
  });

  return (
    <TabScreenWrapper>
      <View style={{ flex: 1 }}>
        <ScrollView
          showsVerticalScrollIndicator={false}
          className="flex-1"
          contentContainerStyle={{ paddingBottom: TAB_BOTTOM_SPACING }}
        >
          <View className="px-4 pt-4 pb-2 relative">
            <ScreenHeader
              title="Discover"
              subtitle="Find new places and dishes to try"
              showMenuButton
            />

            <View
              ref={(ref) => {
                (search.searchBarRef as React.MutableRefObject<View | null>).current = ref;
                registerTarget('discover-search-bar', ref);
              }}
              onLayout={search.handleSearchBarLayout}
              style={{ zIndex: 1001, elevation: 1001 }}
            >
              <SearchBar
                value={search.searchQuery}
                onChangeText={search.handleSearchChange}
                placeholder="Search restaurants, dishes, or users"
                onSubmit={actions.handleSearchSubmit}
                onFocus={search.handleSearchFocus}
                onBlur={search.handleSearchBlur}
              />
            </View>
          </View>

          {checklist.isVisible && !checklist.isLoading && (
            <View className="px-4 pb-2">
              <ProgressiveChecklist
                title="Getting Started"
                items={checklist.items}
                onDismiss={checklist.dismiss}
                onItemPress={(item) => {
                  const def = checklist.items.find((i) => i.id === item.id);
                  if (def?.route) router.push(def.route as any);
                }}
              />
            </View>
          )}

          <View ref={(ref) => registerTarget('discover-cuisine-section', ref)}>
            {data.isCuisinesLoading ? (
              <HorizontalSection title="Categories">
                <View className="px-4 py-8 items-center">
                  <ActivityIndicator size="small" color={theme.colors.primary} />
                </View>
              </HorizontalSection>
            ) : data.cuisines.length > 0 ? (
              <HorizontalSection title="Categories">
                {data.cuisines.map((cuisine) => (
                  <CuisineChip key={cuisine} cuisine={cuisine} />
                ))}
              </HorizontalSection>
            ) : null}
          </View>

          <SuggestedFriends />

          <View ref={(ref) => registerTarget('discover-trending-section', ref)}>
            <RestaurantSection
              title="Trending"
              restaurants={data.trendingBusinesses}
              isLoading={data.isTrendingLoading}
              onSeeAll={actions.handleTrendingSeeAll}
            />
          </View>

          <RestaurantSection
            title="Nearby"
            restaurants={data.nearbyRestaurants}
            isLoading={data.isNearbyLoading}
            onSeeAll={actions.handleNearbySeeAll}
          />

          {data.showEmptyState && <DiscoverEmptyState onAction={actions.handleLeaveReview} />}
        </ScrollView>

        <ExistingUserTastePrompt
          visible={existingUserPrompt.isVisible}
          onClose={() => {}}
        />

        <SearchTypeahead
          showTypeahead={search.showTypeahead}
          searchBarBottom={search.searchBarBottom}
          dropdownTop={search.dropdownTop}
          isLoading={search.isLoading}
          hasResults={search.hasResults}
          hasBusinessResults={search.hasBusinessResults}
          hasUserResults={search.hasUserResults}
          hasMenuItemResults={search.hasMenuItemResults}
          mappedResults={search.mappedResults}
          mappedUserResults={search.mappedUserResults}
          menuItemSearchResults={search.menuItemSearchResults}
          coords={coords}
          units={units}
          onBackdropPress={actions.handleBackdropPress}
          onBusinessPress={actions.handleBusinessPress}
          onUserPress={actions.handleUserPress}
          onMenuItemPress={actions.handleMenuItemPress}
          onMoreResultsPress={actions.handleMoreResultsPress}
        />
      </View>
    </TabScreenWrapper>
  );
}
